<template>
  <div ref="scrollContainer" class="discovery-grid-tab" @scroll="handleScroll">
    <b-overlay
      :show="loading && users.length === 0"
      no-center
      variant="transparent"
      :opacity="0.8"
      rounded="sm"
    >
      <template #overlay>
        <loading-spinner />
      </template>

      <div class="discovery-container">
        <!-- Greeting & Search Header -->
        <section class="discovery-header">
          <div class="header-titles d-flex align-items-center">
            <div>
              <h1 class="main-title">
                {{ $t('For You') || 'For you' }}, <span class="highlight-user">{{ currentUserName }}</span>
              </h1>
              <p class="subtitle">
                {{ filteredUsers.length }} {{ $t('companions waiting to meet you.') || 'companions waiting to meet you.' }}
              </p>
            </div>
          </div>

          <div class="header-search-actions d-flex align-items-center">
            <div class="search-input-box">
              <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                class="search-input"
                :placeholder="$t('Search companions...') || 'Search companions...'"
              >
              <button v-if="searchQuery" class="clear-search-btn" @click="searchQuery = ''">
                &times;
              </button>
            </div>
          </div>
        </section>

        <!-- Featured Carousel Hero Section -->
        <section
          v-if="featuredUsers.length > 0"
          class="featured-section"
          @mouseenter="stopFeaturedTimer"
          @mouseleave="startFeaturedTimer"
        >
          <div class="featured-carousel-wrapper">
            <!-- Main Featured Banner with Slide Left Transition -->
            <div class="featured-banner-container">
              <transition name="slide-left" mode="out-in">
                <div
                  v-if="currentFeaturedUser"
                  :key="'featured-' + (currentFeaturedUser.id || currentFeaturedUser.uuid || featuredIndex)"
                  class="featured-banner-card"
                >
                  <!-- Left: Detailed Profile Info -->
                  <div class="banner-info">
                    <!-- Featured Pill Badge with Star -->
                    <div class="featured-badge-pill">
                      <svg class="star-icon" width="13" height="13" viewBox="0 0 24 24" fill="#ea03b2" stroke="#ea03b2" stroke-width="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                      <span>{{ $t('FEATURED') || 'FEATURED' }}</span>
                    </div>

                    <!-- Country Code + User Name, Age -->
                    <h2 class="featured-user-title">
                      <span v-if="currentFeaturedCountryCode" class="country-code">{{ currentFeaturedCountryCode }}</span>
                      <span class="user-name-age">{{ currentFeaturedUser.user_name }}<template v-if="currentFeaturedUser.age">, {{ currentFeaturedUser.age }}</template></span>
                    </h2>

                    <!-- Location Row -->
                    <div class="featured-location-row">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <span>{{ getFeaturedLocation(currentFeaturedUser) }}</span>
                    </div>

                    <!-- Likes Row -->
                    <div v-if="currentFeaturedLikes" class="featured-likes-row">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#ea03b2" stroke="#ea03b2" stroke-width="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                      </svg>
                      <span>{{ currentFeaturedLikes }} {{ $t('likes') || 'likes' }}</span>
                    </div>

                    <!-- Bio Text -->
                    <p class="featured-bio-desc">
                      {{ getFeaturedBio(currentFeaturedUser) }}
                    </p>

                    <!-- Empty Tag Spacer preserving banner height without tags -->
                    <div class="featured-tags-spacer"></div>

                    <!-- Action Buttons Row -->
                    <div class="banner-cta-group">
                      <button class="cta-chat-pill-btn" @click.stop="chat(currentFeaturedUser)">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <span>{{ $t('Chat now') || 'Chat now' }}</span>
                        <svg class="arrow-right-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </button>

                      <button class="cta-profile-pill-btn" @click.stop="goToProfile(currentFeaturedUser)">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        <span>{{ $t('View profile') || 'View profile' }}</span>
                      </button>
                    </div>
                  </div>

                  <!-- Right: Photo with Seamless Fade & Overlays -->
                  <div class="banner-photo-wrapper" @click="goToProfile(currentFeaturedUser)">
                    <img
                      :src="currentFeaturedUser.profile_photo_url"
                      :alt="currentFeaturedUser.user_name"
                      class="banner-photo-img"
                    >

                    <!-- Önceki / sonraki okları -->
                    <template v-if="featuredUsers.length > 1">
                      <button
                        type="button"
                        class="banner-nav-btn banner-nav-prev"
                        :title="$t('Previous')"
                        @click.stop="prevFeatured"
                      >
                        <feather-icon icon="ChevronLeftIcon" size="20" />
                      </button>
                      <button
                        type="button"
                        class="banner-nav-btn banner-nav-next"
                        :title="$t('Next')"
                        @click.stop="nextFeatured"
                      >
                        <feather-icon icon="ChevronRightIcon" size="20" />
                      </button>
                    </template>

                    <!-- Sağ üstte öne çıkan kullanıcıların küçük avatarları: kimde olduğun ve sıradakiler net görünür.
                         (Önceden yanda ikinci büyük fotoğraf ve noktalar vardı; iki farklı yüz kafa karıştırıyordu.) -->
                    <div class="banner-vertical-dots">
                      <button
                        v-for="(fUser, idx) in featuredUsers"
                        :key="'vdot-' + idx"
                        type="button"
                        class="vertical-dot"
                        :class="{ active: idx === featuredIndex }"
                        :title="fUser.user_name"
                        @click.stop="selectFeaturedIndex(idx)"
                      >
                        <img
                          v-if="fUser.profile_photo_url && !brokenPhotos[fUser.id || fUser.uuid]"
                          :src="fUser.profile_photo_url"
                          :alt="fUser.user_name"
                          @error="onPhotoError(fUser)"
                        >
                        <span v-else>{{ (fUser.user_name || '?').charAt(0).toUpperCase() }}</span>
                      </button>
                    </div>

                  </div>
                </div>
              </transition>
            </div>
          </div>
        </section>

        <!-- Filter Category Pills (Rush Style) -->
        <section class="filters-section">
          <div class="filter-pills-row">
            <button
              v-for="cat in categories"
              :key="cat.id"
              class="category-pill"
              :class="{ active: selectedCategory === cat.id }"
              @click="selectCategory(cat.id)"
            >
              <!-- Icon for All / Tümü -->
              <svg v-if="cat.id === 'all'" class="cat-pill-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <span>{{ $t(cat.label) || cat.label }}</span>
            </button>
          </div>
        </section>

        <!-- Responsive Profiles Grid (4 Columns Desktop / 2 Mobile) -->
        <section class="grid-section">
          <div class="profiles-grid">
            <div
              v-for="(user, i) in filteredUsers"
              :key="user.id || user.uuid || i"
              class="rush-card"
              @click="goToProfile(user)"
            >
              <!-- Card Photo Background -->
              <div class="card-photo-box">
                <img
                  v-if="user.profile_photo_url && !brokenPhotos[user.id || user.uuid]"
                  :src="user.profile_photo_url"
                  :alt="user.user_name"
                  class="photo-img"
                  loading="lazy"
                  @error="onPhotoError(user)"
                >
                <!-- Resim yoksa ya da yüklenemezse kırık resim simgesi yerine baş harf -->
                <div v-else class="photo-fallback">
                  {{ (user.user_name || user.userName || '?').charAt(0).toUpperCase() }}
                </div>
              </div>

              <!-- Top Row Badges -->
              <div class="card-badges-top">
                <!-- Status Badge -->
                <div
                  v-if="user.is_connected && user.busy_in_call"
                  class="status-pill busy"
                >
                  <span class="status-dot" />
                  <span class="status-text">{{ $t('Busy') }}</span>
                </div>
                <div v-else-if="user.is_connected" class="status-pill online">
                  <span class="status-dot"></span>
                  <span class="status-text">{{ $t('ONLINE') || 'ONLINE' }}</span>
                </div>
                <div v-else-if="isRecentlySeen(user.last_seen_time)" class="status-pill recently">
                  <span class="status-dot recently"></span>
                  <span class="status-text">{{ $t('ONLINE RECENTLY') || 'ONLINE RECENTLY' }}</span>
                </div>
                <div v-else class="status-pill offline">
                  <span class="status-dot offline"></span>
                  <span class="status-text">{{ $t('OFFLINE') || 'OFFLINE' }}</span>
                </div>

                <!-- AI / Real Badge -->
                <div class="type-pill" :class="user.is_ai ? 'is-ai' : 'is-real'">
                  <span v-if="user.is_ai" class="ai-star">✨</span>
                  <span>{{ user.is_ai ? 'AI' : 'REAL' }}</span>
                </div>
              </div>

              <!-- Bottom Content & Action Bar -->
              <div class="card-info-bottom">
                <div class="user-primary-row">
                  <span v-if="user.country" class="country-badge">{{ user.country.toUpperCase() }}</span>
                  <h3 class="user-display-name">
                    {{ user.user_name }}<span v-if="user.age">, {{ user.age }}</span>
                  </h3>
                </div>

                <div class="user-secondary-row">
                  <span class="sub-info">
                    {{ user.subscribers_count ? `${user.subscribers_count} ${$t('subscribers') || 'subscribers'}` : getCountryEmoji(user.country) }}
                  </span>
                </div>

                <!-- Rush Action Button Bar -->
                <div class="card-actions-bar">
                  <!-- Sesli arama: AI kullanıcılar veya cameraOn false + voiceOn true -->
                  <button
                    v-if="getCallMode(user) === 'voice'"
                    class="action-pill-btn call-action voice-action"
                    @click.stop="call(user)"
                    :title="$t('Voice Call') || 'Voice call'"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    <span>{{ $t('Voice Call') || 'Voice call' }}</span>
                  </button>

                  <!-- Görüntülü arama: cameraOn true -->
                  <button
                    v-else-if="getCallMode(user) === 'video'"
                    class="action-pill-btn call-action video-action"
                    @click.stop="videoCall(user)"
                    :title="$t('Video Call') || 'Video call'"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polygon points="23 7 16 12 23 17 23 7"></polygon>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                    </svg>
                    <span>{{ $t('Video Call') || 'Video call' }}</span>
                  </button>

                  <!-- Chat Button -->
                  <button
                    class="action-circle-btn chat-action"
                    @click.stop="chat(user)"
                    :title="$t('Chat') || 'Chat'"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </button>

                  <!-- Like / Heart Button -->
                  <button
                    class="action-circle-btn like-action"
                    :class="{ 'is-liked': user.is_followed || user.is_liked }"
                    @click.stop="toggleFollow(user, i)"
                    :title="$t('Like') || 'Like'"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" :fill="(user.is_followed || user.is_liked) ? '#ef4444' : 'none'" :stroke="(user.is_followed || user.is_liked) ? '#ef4444' : 'currentColor'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="!loading && filteredUsers.length === 0" class="empty-state">
            <div class="empty-icon-box">🔍</div>
            <h3>{{ $t('No companions found') || 'No companions found' }}</h3>
            <p>{{ $t('Try changing your search or filter options.') || 'Try changing your search or filter options.' }}</p>
          </div>
        </section>

        <!-- Load More Spinner -->
        <div v-if="loading && users.length > 0" class="load-more-spinner">
          <b-spinner small variant="primary" />
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { BOverlay, BSpinner } from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import { getUser } from '@/auth/utils'
import { showNotify, throwDefaultError } from '@/helpers/helpers'
import countries from '@/data/countries.json'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

export default {
  name: 'DiscoveryGrid',
  components: {
    BOverlay,
    BSpinner,
    LoadingSpinner,
  },
  data() {
    return {
      loading: false,
      users: [],
      offset: 0,
      limit: 50,
      allLoaded: false,
      searchQuery: '',
      selectedCategory: 'all',
      featuredIndex: 0,
      featuredTimer: null,
      // Yüklenemeyen profil resimleri (id -> true)
      brokenPhotos: {},
      categories: [
        { id: 'all', label: 'All' },
        { id: 'ai', label: 'AI' },
        { id: 'real', label: 'Real' },
        { id: 'online', label: 'Online' },
      ],
    }
  },
  computed: {
    currentUser() {
      return getUser() || {}
    },
    currentUserName() {
      return this.currentUser.userName || this.currentUser.name || 'User'
    },
    unreadCount() {
      return this.$store.getters['chat/totalUnreadCount'] || 1
    },
    featuredUsers() {
      if (!this.users || this.users.length === 0) return []
      return this.users.slice(0, 5)
    },
    currentFeaturedUser() {
      if (this.featuredUsers.length === 0) return null
      return this.featuredUsers[this.featuredIndex] || this.featuredUsers[0]
    },
    currentFeaturedCountryCode() {
      if (!this.currentFeaturedUser) return ''
      return (this.currentFeaturedUser.country || '').toUpperCase()
    },
    currentFeaturedCounter() {
      const total = this.featuredUsers.length || 5
      const current = (this.featuredIndex % total) + 1
      return `${current}/${total}`
    },
    nextFeaturedUser() {
      if (this.featuredUsers.length <= 1) return null
      return this.featuredUsers[(this.featuredIndex + 1) % this.featuredUsers.length]
    },
    currentFeaturedLikes() {
      if (this.currentFeaturedUser && this.currentFeaturedUser.likes_count) {
        return this.currentFeaturedUser.likes_count
      }
      return 0
    },
    filteredUsers() {
      let list = this.users || []

      // Category filtering
      if (this.selectedCategory === 'ai') {
        list = list.filter(u => u.is_ai)
      } else if (this.selectedCategory === 'real') {
        list = list.filter(u => !u.is_ai)
      } else if (this.selectedCategory === 'online') {
        list = list.filter(u => u.is_connected)
      } else if (this.selectedCategory && this.selectedCategory !== 'all') {
        const cat = this.categories.find(c => c.id === this.selectedCategory)
        const targetMoodId = cat ? cat.moodId : null
        const targetName = (cat ? (cat.rawName || cat.label) : this.selectedCategory).toLowerCase()

        list = list.filter(u => {
          if (targetMoodId && (u.mood_id === targetMoodId || (u.mood && u.mood.id === targetMoodId))) {
            return true
          }
          if (u.mood_name && u.mood_name.toLowerCase() === targetName) {
            return true
          }
          if (u.mood && typeof u.mood === 'string' && u.mood.toLowerCase() === targetName) {
            return true
          }
          if (u.mood && u.mood.name && u.mood.name.toLowerCase() === targetName) {
            return true
          }
          const text = `${u.bio || ''} ${u.about || ''} ${u.user_name || ''}`.toLowerCase()
          return text.includes(targetName)
        })
      }

      // Search Query filtering
      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const q = this.searchQuery.trim().toLowerCase()
        list = list.filter(u => {
          const name = (u.user_name || '').toLowerCase()
          const country = (u.country || '').toLowerCase()
          return name.includes(q) || country.includes(q)
        })
      }

      return list
    },
  },
  created() {
    // Use the existing global socket handler; do not add another socket connection/listener.
    this.presenceTimestamps = new Map()
    this.pendingPresenceEvents = null
    this.discoveryDisposed = false
    this.discoveryRefreshPending = false
    this.discoveryRefreshTimer = null
    this.lastDiscoveryFetchAt = 0
    this.backgroundRefreshing = false
    this.unsubscribePresence = this.$store.subscribe(mutation => {
      if (mutation.type !== 'users/addUser') return
      const event = mutation.payload
      if (!event || !['USER_ONLINE', 'USER_OFFLINE', 'USER_BUSY', 'USER_AVAILABLE'].includes(event.type)) return

      // Preserve events received while a list response is in flight, including users
      // whose cards have not arrived yet. Bound this temporary buffer.
      if (this.pendingPresenceEvents) {
        if (this.pendingPresenceEvents.length >= 1000) this.pendingPresenceEvents.shift()
        this.pendingPresenceEvents.push(event)
      }
      if (event.type === 'USER_ONLINE' && !this.users.some(user => user.uuid === event.userUuid)) {
        this.discoveryRefreshPending = true
        this.scheduleDiscoveryRefresh()
      }
      this.applyPresenceEvent(event, this.users)
    })
    document.addEventListener('visibilitychange', this.onDiscoveryVisibilityChange)
    this.getUsers()
    this.fetchMoodCategories()
    this.startFeaturedTimer()
  },
  beforeDestroy() {
    this.discoveryDisposed = true
    if (this.discoveryRefreshTimer !== null) clearTimeout(this.discoveryRefreshTimer)
    document.removeEventListener('visibilitychange', this.onDiscoveryVisibilityChange)
    if (this.unsubscribePresence) this.unsubscribePresence()
    this.pendingPresenceEvents = null
    this.presenceTimestamps.clear()
    this.stopFeaturedTimer()
  },
  methods: {
    onDiscoveryVisibilityChange() {
      if (document.hidden) {
        if (this.discoveryRefreshTimer !== null) clearTimeout(this.discoveryRefreshTimer)
        this.discoveryRefreshTimer = null
      } else {
        this.scheduleDiscoveryRefresh()
      }
    },
    scheduleDiscoveryRefresh() {
      if (this.discoveryDisposed || document.hidden || !this.discoveryRefreshPending
        || this.discoveryRefreshTimer !== null || this.loading || this.backgroundRefreshing) return

      // Coalesce online bursts into one trailing refresh; never overlap pagination.
      const waitMs = Math.max(0, 30000 - (Date.now() - this.lastDiscoveryFetchAt))
      this.discoveryRefreshTimer = setTimeout(() => {
        this.discoveryRefreshTimer = null
        if (this.discoveryDisposed || document.hidden || this.loading || this.backgroundRefreshing) return
        // Pagination may have started after this timer was scheduled.
        if (Date.now() - this.lastDiscoveryFetchAt < 30000) {
          this.scheduleDiscoveryRefresh()
          return
        }
        this.discoveryRefreshPending = false
        this.getUsers(false, true)
      }, waitMs)
    },
    mergeDiscoveryUsers(list, background) {
      const keyOf = user => user.uuid || String(user.id)
      const existingById = new Map(this.users.map(user => [keyOf(user), user]))
      list.forEach(user => {
        const key = keyOf(user)
        const existing = existingById.get(key)
        if (existing) {
          // A silent refresh only updates presence, preserving local favorites and card order.
          const fields = background ? ['is_connected', 'last_seen_time', 'busy_in_call'] : Object.keys(user)
          fields.forEach(field => {
            if (Object.prototype.hasOwnProperty.call(user, field)) this.$set(existing, field, user[field])
          })
        } else {
          this.users.push(user)
          existingById.set(key, user)
        }
      })
    },
    applyPresenceEvent(event, users, timestamps = this.presenceTimestamps) {
      const uuid = event.userUuid
      if (!uuid) return
      const timestamp = Number(event.timestamp)
      const hasTimestamp = Number.isFinite(timestamp) && timestamp > 0
      if (hasTimestamp && timestamp < (timestamps.get(uuid) || 0)) return

      const targets = users.filter(user => user.uuid === uuid && !user.is_ai)
      if (targets.length === 0) return
      if (hasTimestamp) timestamps.set(uuid, timestamp)

      targets.forEach(user => {
        if (event.type === 'USER_ONLINE') {
          this.$set(user, 'is_connected', true)
        } else if (event.type === 'USER_OFFLINE') {
          this.$set(user, 'is_connected', false)
          this.$set(user, 'busy_in_call', false)
          this.$set(user, 'last_seen_time', new Date(hasTimestamp ? timestamp : Date.now()).toISOString())
        } else if (event.type === 'USER_BUSY') {
          this.$set(user, 'busy_in_call', true)
        } else if (event.type === 'USER_AVAILABLE') {
          // Available clears busy state; it does not prove the socket is online.
          this.$set(user, 'busy_in_call', false)
        }
      })
    },
    startFeaturedTimer() {
      this.stopFeaturedTimer()
      this.featuredTimer = setInterval(() => {
        if (this.featuredUsers.length > 0) {
          this.nextFeatured()
        }
      }, 4500)
    },
    stopFeaturedTimer() {
      if (this.featuredTimer) {
        clearInterval(this.featuredTimer)
        this.featuredTimer = null
      }
    },
    nextFeatured() {
      if (this.featuredUsers.length === 0) return
      this.featuredIndex = (this.featuredIndex + 1) % this.featuredUsers.length
    },
    prevFeatured() {
      if (this.featuredUsers.length === 0) return
      this.featuredIndex = this.featuredIndex === 0 ? this.featuredUsers.length - 1 : this.featuredIndex - 1
    },
    selectFeaturedIndex(idx) {
      this.featuredIndex = idx
      this.startFeaturedTimer()
    },
    selectCategory(catId) {
      this.selectedCategory = catId
    },
    handleScroll() {
      const el = this.$refs.scrollContainer
      if (!el || this.loading || this.allLoaded) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
        this.getUsers(false)
      }
    },
    isRecentlySeen(lastSeenTime) {
      if (!lastSeenTime) return false
      return (Date.now() - new Date(lastSeenTime).getTime()) < 15 * 60 * 1000
    },
    getCountryEmoji(code) {
      if (!code) return ''
      const country = countries.find(c => c.code === code.toUpperCase())
      return country ? country.emoji : ''
    },
    getCountryName(code) {
      if (!code) return ''
      const country = countries.find(c => c.code && c.code.toLowerCase() === code.toLowerCase())
      return country ? country.name : code.toUpperCase()
    },
    getFeaturedLocation(user) {
      if (!user) return 'São Paulo, Brazil'
      if (user.city && user.country) return `${user.city}, ${this.getCountryName(user.country)}`
      if (user.city) return user.city
      const countryLocations = {
        br: 'São Paulo, Brazil',
        tr: 'Istanbul, Turkey',
        ua: 'Kyiv, Ukraine',
        us: 'New York, USA',
        de: 'Berlin, Germany',
        fr: 'Paris, France',
        ru: 'Moscow, Russia',
        es: 'Madrid, Spain',
        it: 'Rome, Italy',
        gb: 'London, UK',
      }
      const code = (user.country || 'br').toLowerCase()
      return countryLocations[code] || (this.getCountryName(user.country) || 'São Paulo, Brazil')
    },
    getFeaturedBio(user) {
      if (!user) return ''
      const bio = user.bio
        || (user.profile && user.profile.bio)
        || user.about
        || (user.profile && user.profile.about)
        || user.description
        || (user.profile && user.profile.description)
      if (bio && typeof bio === 'string' && bio.trim().length > 0) {
        return bio.trim()
      }
      return ''
    },
    fetchMoodCategories() {
      useJwt.getChatCommunityMoods()
        .then(response => {
          const apiMoods = response.data && response.data.body ? response.data.body : []
          if (Array.isArray(apiMoods) && apiMoods.length > 0) {
            const baseCategories = [
              { id: 'all', label: 'All' },
              { id: 'ai', label: 'AI' },
              { id: 'real', label: 'Real' },
              { id: 'online', label: 'Online' },
            ]
            const moodPills = apiMoods.map(m => ({
              id: `mood_${m.id}`,
              moodId: m.id,
              rawName: m.name,
              label: m.emoji ? `${m.emoji} ${m.name}` : m.name,
              emoji: m.emoji || '',
            }))
            this.categories = [...baseCategories, ...moodPills]
          }
        })
        .catch(() => {})
    },
    getUsers(clear = true, background = false) {
      if (this.discoveryDisposed || this.loading || this.backgroundRefreshing) return null
      if (clear && !background) {
        this.offset = 0
        this.users = []
        this.allLoaded = false
      }
      this.pendingPresenceEvents = []
      const responseTimestamps = new Map(this.presenceTimestamps)
      this.lastDiscoveryFetchAt = Date.now()
      if (background) this.backgroundRefreshing = true
      else this.loading = true
      return useJwt.getPostsDiscovery(background ? 0 : this.offset, this.limit)
        .then(response => {
          if (this.discoveryDisposed) return
          const list = (response.data.body || [])
          // A response prepared before a socket event must not undo that event.
          const pendingEvents = this.pendingPresenceEvents || []
          pendingEvents.forEach(event => this.applyPresenceEvent(event, list, responseTimestamps))
          list.forEach(user => {
            const timestamp = responseTimestamps.get(user.uuid)
            if (timestamp) this.presenceTimestamps.set(user.uuid, timestamp)
          })
          if (clear && !background) {
            this.users = list
          } else {
            this.mergeDiscoveryUsers(list, background)
          }
          // Silent first-page refreshes must not advance the pagination cursor.
          if (!background) {
            this.offset += list.length
            if (list.length < this.limit) {
              this.allLoaded = true
            }
          }
        })
        .catch(error => {
          if (this.discoveryDisposed) return
          if (background) console.warn('Discovery presence refresh failed', error)
          else throwDefaultError(this.$toast, error)
        })
        .finally(() => {
          this.pendingPresenceEvents = null
          this.loading = false
          this.backgroundRefreshing = false
          this.scheduleDiscoveryRefresh()
        })
    },
    toggleFollow(user, i) {
      const targetUser = user || this.users[i]
      const targetId = targetUser.uuid || targetUser.id
      if (!targetUser.is_followed) {
        useJwt.addFavoriteUser(targetId)
          .then(() => {
            showNotify(this.$toast, this.$t('Liking successful') || 'Success', 'success')
            this.$set(targetUser, 'is_followed', true)
            this.$set(targetUser, 'is_liked', true)
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
      } else {
        useJwt.removeFavoriteUser(targetId)
          .then(() => {
            showNotify(this.$toast, this.$t('Unliking successful') || 'Removed', 'success')
            this.$set(targetUser, 'is_followed', false)
            this.$set(targetUser, 'is_liked', false)
          })
          .catch(error => {
            throwDefaultError(this.$toast, error)
          })
      }
    },
    goToProfile(user) {
      if (!user) return
      this.$router.push({
        name: 'users.show',
        params: { id: user.uuid || user.id },
      })
    },
    chat(user) {
      if (!user) return
      this.$router.push({
        name: 'chat',
        params: { userId: user.uuid || user.id },
      })
    },
    onPhotoError(user) {
      this.$set(this.brokenPhotos, user.id || user.uuid, true)
    },
    // Kartta hangi arama butonu gösterilecek:
    // AI -> sesli; cameraOn true -> görüntülü; cameraOn false ve voiceOn true -> sesli; ikisi de kapalı -> buton yok.
    // Alanlar hiç gelmezse eski davranış (görüntülü) korunur.
    getCallMode(user) {
      if (!user) return null
      if (user.is_ai || user.isAi) return 'voice'
      const profile = user.profile || {}
      const pick = (...values) => values.find(v => v !== undefined && v !== null)
      const cameraOn = pick(user.camera_on, user.cameraOn, profile.cameraOn)
      const voiceOn = pick(user.voice_on, user.voiceOn, profile.voiceOn)
      if (cameraOn === undefined && voiceOn === undefined) return 'video'
      if (cameraOn) return 'video'
      if (voiceOn) return 'voice'
      return null
    },
    call(user) {
      if (!user) return
      // AI kullanıcılar profil sayfasındaki gibi LiveKit sesli görüşme akışıyla aranır
      // (eski Agora akışı AI kullanıcılar için çalışmıyordu)
      if (user.is_ai || user.isAi) {
        this.$root.$emit('ai-voice-call:start', {
          ...user,
          uuid: user.uuid || user.id,
          id: user.uuid || user.id,
          user_name: user.user_name || user.userName,
          image: user.profile_photo_url,
          profile: { image: user.profile_photo_url },
        })
        return
      }
      this.$store.dispatch('app/setModalCallType', 'audio')
      this.$store.dispatch('app/setCallDetails', {
        callSource: 'discovery_grid',
        userOrder: 0,
      })
      this.$store.dispatch('app/setModalCall', false)
      setTimeout(() => {
        this.$store.dispatch('app/setModalCallUser', {
          ...user,
          id: user.uuid || user.id,
          userName: user.user_name || user.userName,
          profile: {
            image: user.profile_photo_url,
            country: user.country,
            onlineStatus: user.is_connected ? 'online' : 'offline',
          },
        })
        this.$store.dispatch('app/setModalCall', true)
      }, 33)
    },
    videoCall(user) {
      if (!user) return
      this.$store.dispatch('app/setModalCallType', 'video')
      this.$store.dispatch('app/setCallDetails', {
        callSource: 'discovery_grid',
        userOrder: 0,
      })
      this.$store.dispatch('app/setModalCall', false)
      setTimeout(() => {
        this.$store.dispatch('app/setModalCallUser', {
          ...user,
          id: user.uuid || user.id,
          userName: user.user_name || user.userName,
          profile: {
            image: user.profile_photo_url,
            country: user.country,
            onlineStatus: user.is_connected ? 'online' : 'offline',
          },
        })
        this.$store.dispatch('app/setModalCall', true)
      }, 33)
    },
    goToHome() {
      this.$router.push('/').catch(() => {})
    },
    goToChat() {
      this.$router.push({ name: 'chat' }).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap');

.discovery-grid-tab {
  position: relative;
  width: 100%;
  min-height: auto;
  overflow: visible;
  background: transparent !important;
  color: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-overflow-scrolling: touch;
}

.discovery-container {
  width: 100%;
  max-width: 1320px;
  margin: 0 auto;
  padding: 32px 24px 70px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px 12px calc(80px + env(safe-area-inset-bottom, 0px));
  }
}

/* Header & Search */
.discovery-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 18px;
  }

  .header-titles {
    display: flex;
    align-items: center;
    gap: 12px;

    .header-heart-icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        filter: drop-shadow(0 4px 10px rgba(192, 38, 211, 0.3));
      }
    }

    .main-title {
      font-size: 24px;
      font-weight: 800;
      color: #ffffff;
      margin: 0 0 3px 0;
      letter-spacing: -0.4px;
      line-height: 1.2;

      @media (max-width: 768px) {
        font-size: 20px;
      }

      .highlight-user {
        // Gradyan yazı (background-clip:text) bazen kutu olarak görünüyordu; düz renk kullanılıyor
        color: #e879f9;
      }
    }

    .subtitle {
      font-size: 13.5px;
      color: rgba(255, 255, 255, 0.72);
      margin: 0;

      @media (max-width: 768px) {
        font-size: 12.5px;
      }
    }
  }

  .header-search-actions {
    display: flex;
    align-items: center;
    gap: 10px;

    @media (max-width: 768px) {
      width: 100%;
    }

    .search-input-box {
      position: relative;
      display: flex;
      align-items: center;
      width: 290px;

      @media (max-width: 768px) {
        flex: 1;
        width: auto;
      }

      .search-icon {
        position: absolute;
        left: 14px;
        color: #cbd5e1;
        stroke: #cbd5e1;
        z-index: 2;
        pointer-events: none;
        transition: color 0.2s ease, stroke 0.2s ease;
      }

      .search-input {
        width: 100%;
        height: 42px;
        padding: 0 36px 0 42px;
        background: rgba(255, 255, 255, 0.12);
        border: 1px solid rgba(255, 255, 255, 0.24);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        border-radius: 9999px;
        font-size: 13.5px;
        color: #ffffff;
        outline: none;
        transition: all 0.2s ease;

        &::placeholder {
          color: rgba(255, 255, 255, 0.65);
        }

        &:focus {
          background: rgba(255, 255, 255, 0.18);
          border-color: #c084fc;
          box-shadow: 0 0 0 3px rgba(192, 132, 252, 0.35);
        }
      }

      &:focus-within .search-icon {
        color: #ffffff;
        stroke: #ffffff;
      }

      .clear-search-btn {
        position: absolute;
        right: 12px;
        background: none;
        border: none;
        font-size: 18px;
        color: rgba(255, 255, 255, 0.5);
        cursor: pointer;
        line-height: 1;

        &:hover {
          color: #ffffff;
        }
      }
    }

    .header-icon-btn {
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.16);
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      position: relative;
      flex-shrink: 0;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.18);
        color: #ffffff;
      }

      .notification-badge-dot {
        position: absolute;
        top: 9px;
        right: 10px;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #ea03b2;
        border: 1.5px solid #ffffff;
      }
    }
  }
}

/* Featured Hero Banner */
.featured-section {
  position: relative;
  z-index: 2;
  margin-bottom: 26px;

  .featured-carousel-wrapper {
    display: flex;
    gap: 16px;
    align-items: stretch;
    position: relative;
    width: 100%;
    height: 330px; // Strict standard height for desktop

    @media (max-width: 1199px) {
      height: 310px;
      gap: 14px;
    }

    @media (max-width: 991px) {
      height: 290px;
    }

    @media (max-width: 767px) {
      height: 275px;
    }

    @media (max-width: 575px) {
      height: 372px; // Mobil: foto (185) + bilgi alanı (187)
    }

    /* Left Side Mini Card Preview */
    .featured-side-card {
      width: 175px;
      min-width: 175px;
      max-width: 175px;
      height: 100%;
      border-radius: 26px;
      overflow: hidden;
      cursor: pointer;
      position: relative;
      background: #11172b;
      flex-shrink: 0;
      box-shadow: 0 10px 28px rgba(17, 23, 43, 0.25);
      transition: transform 0.25s ease, box-shadow 0.25s ease;

      @media (max-width: 1199px) {
        width: 150px;
        min-width: 150px;
        max-width: 150px;
      }

      @media (max-width: 991px) {
        display: none;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 14px 34px rgba(17, 23, 43, 0.35);
      }

      .side-card-img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center 20%;
        display: block;
      }

      // Önizleme olduğu belli olsun diye alttan koyulaşan gölge
      .side-card-shade {
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(10, 6, 30, 0.15) 40%, rgba(10, 6, 30, 0.85) 100%);
        z-index: 1;
      }

      .side-card-next-label {
        position: absolute;
        left: 14px;
        right: 14px;
        bottom: 16px;
        z-index: 2;
        display: flex;
        flex-direction: column;
        gap: 2px;
        color: #fff;

        span {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.6px;
          opacity: 0.75;
        }

        strong {
          font-size: 15px;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .side-card-counter {
        position: absolute;
        top: 14px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.72);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        color: #ffffff;
        font-size: 11.5px;
        font-weight: 700;
        padding: 3px 12px;
        border-radius: 9999px;
        letter-spacing: 0.5px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
        z-index: 2;
      }
    }

    /* Main Banner Container - Guaranteed Constant Height */
    .featured-banner-container {
      flex: 1;
      min-width: 0;
      height: 100%;
      position: relative;
      overflow: hidden;
      border-radius: 28px;

      @media (max-width: 767px) {
        border-radius: 22px;
      }

      @media (max-width: 575px) {
        border-radius: 20px;
      }
    }

    /* Leftward animated slide transition */
    .slide-left-enter-active,
    .slide-left-leave-active {
      transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.4s ease;
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
    .slide-left-enter {
      opacity: 0;
      transform: translateX(45px);
    }
    .slide-left-leave-to {
      opacity: 0;
      transform: translateX(-45px);
    }

    .featured-banner-card {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      border-radius: inherit;
      background: radial-gradient(circle at 62% 28%, rgba(217, 70, 239, 0.42) 0%, transparent 48%),
                  radial-gradient(circle at 45% 75%, rgba(168, 85, 247, 0.3) 0%, transparent 55%),
                  linear-gradient(135deg, #11172b 0%, #1e1b4b 32%, #420f66 68%, #581c87 100%);
      box-shadow: 0 16px 40px rgba(17, 23, 43, 0.35);
      color: #ffffff;
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      overflow: hidden;

      @media (max-width: 575px) {
        flex-direction: column-reverse;
      }

      .banner-info {
        position: relative;
        z-index: 2;
        width: 55%;
        max-width: 580px;
        min-width: 0;
        height: 100%;
        padding: 24px 30px;
        display: flex;
        flex-direction: column;
        justify-content: center;

        @media (max-width: 1199px) {
          padding: 20px 24px;
          width: 58%;
        }

        @media (max-width: 991px) {
          width: 58%;
          padding: 18px 22px;
        }

        @media (max-width: 767px) {
          width: 60%;
          padding: 16px 18px;
        }

        @media (max-width: 575px) {
          /* Mobil: bilgi alanı fotoğrafın üstünde, alttan yukarı doğru kaybolan gradyanla;
             fotoğrafın üst ve orta kısmı açık kalır */
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          max-width: 100%;
          height: auto;
          padding: 56px 16px 14px;
          justify-content: flex-end;
          background: linear-gradient(
            to bottom,
            rgba(17, 12, 43, 0) 0%,
            rgba(17, 12, 43, 0.55) 38%,
            rgba(17, 12, 43, 0.9) 72%,
            rgba(17, 12, 43, 0.96) 100%
          );
          text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);

          .banner-cta-group { margin-top: 10px; }
        }

        .featured-badge-pill {
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          color: #ffffff;
          padding: 4px 12px;
          border-radius: 9999px;
          width: fit-content;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          margin-bottom: 10px;

          @media (max-width: 575px) {
            padding: 3px 9px;
            font-size: 10px;
            letter-spacing: 1px;
            margin-bottom: 4px;
          }

          .star-icon {
            flex-shrink: 0;
          }
        }

        .featured-user-title {
          font-size: 26px;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 6px 0;
          display: flex;
          align-items: baseline;
          gap: 8px;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          @media (max-width: 1199px) {
            font-size: 23px;
          }

          @media (max-width: 767px) {
            font-size: 20px;
            margin-bottom: 4px;
          }

          @media (max-width: 575px) {
            font-size: 19px;
            margin-bottom: 3px;
          }

          .country-code {
            font-size: 21px;
            font-weight: 900;
            letter-spacing: 1px;
            color: rgba(255, 255, 255, 0.95);

            @media (max-width: 767px) {
              font-size: 17px;
            }
          }

          .user-name-age {
            font-weight: 800;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .featured-location-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.78);
          margin-bottom: 4px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          @media (max-width: 767px) {
            font-size: 12px;
            margin-bottom: 3px;
          }

          svg {
            stroke: rgba(255, 255, 255, 0.75);
            flex-shrink: 0;
          }
        }

        .featured-likes-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 10px;

          @media (max-width: 767px) {
            font-size: 12px;
            margin-bottom: 6px;
          }

          svg {
            flex-shrink: 0;
          }
        }

        .featured-bio-desc {
          font-size: 13px;
          line-height: 1.4;
          color: rgba(255, 255, 255, 0.88);
          margin: 0 0 14px 0;
          max-width: 420px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          height: 2.8em; // Strictly uniform height for all users

          @media (max-width: 767px) {
            font-size: 12px;
            margin-bottom: 8px;
            height: 2.6em;
            -webkit-line-clamp: 2;
          }

          @media (max-width: 575px) {
            font-size: 11.5px;
            margin-bottom: 6px;
            height: auto;
            max-height: 2.6em;
            -webkit-line-clamp: 2;

            &:empty { display: none; }
          }
        }

        .featured-tags-spacer {
          height: 28px;
          margin-bottom: 18px;

          @media (max-width: 767px) {
            /* Mobilde boşluk bırakan yer tutucu gizlenir */
            display: none;
          }
        }

        .banner-cta-group {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: nowrap;

          @media (max-width: 767px) {
            gap: 8px;
          }

          @media (max-width: 575px) {
            width: 100%;
            gap: 8px;
          }

          .cta-chat-pill-btn {
            background: linear-gradient(135deg, #d946ef 0%, #ea03b2 50%, #f43f5e 100%);
            color: #ffffff;
            border: none;
            border-radius: 9999px;
            padding: 9.5px 22px;
            font-size: 13px;
            font-weight: 700;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            box-shadow: 0 4px 16px rgba(234, 3, 178, 0.42);
            cursor: pointer;
            transition: all 0.2s ease;
            white-space: nowrap;
            flex-shrink: 0;

            @media (max-width: 767px) {
              padding: 7.5px 15px;
              font-size: 12px;
              gap: 5px;
            }

            @media (max-width: 575px) {
              flex: 1;
              padding: 8px 10px;
              font-size: 11.5px;
            }

            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 22px rgba(234, 3, 178, 0.58);
            }

            &:active {
              transform: translateY(0);
            }

            .arrow-right-icon {
              transition: transform 0.2s ease;
            }

            &:hover .arrow-right-icon {
              transform: translateX(3px);
            }
          }

          .cta-profile-pill-btn {
            background: rgba(255, 255, 255, 0.08);
            border: 1.5px solid rgba(255, 255, 255, 0.35);
            color: #ffffff;
            border-radius: 9999px;
            padding: 9px 20px;
            font-size: 13px;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            cursor: pointer;
            transition: all 0.2s ease;
            backdrop-filter: blur(8px);
            white-space: nowrap;
            flex-shrink: 0;

            @media (max-width: 767px) {
              padding: 7px 13px;
              font-size: 12px;
              gap: 5px;
            }

            @media (max-width: 575px) {
              flex: 1;
              padding: 7.5px 10px;
              font-size: 11.5px;
            }

            &:hover {
              background: rgba(255, 255, 255, 0.16);
              border-color: rgba(255, 255, 255, 0.6);
            }

            &:active {
              transform: scale(0.98);
            }
          }
        }
      }

      /* Photo Container - Completely decoupled from image aspect ratio */
      .banner-photo-wrapper {
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 48%;
        height: 100%;
        overflow: hidden;
        cursor: pointer;

        @media (max-width: 1199px) {
          width: 46%;
        }

        @media (max-width: 991px) {
          width: 45%;
        }

        @media (max-width: 767px) {
          width: 43%;
        }

        @media (max-width: 575px) {
          /* Mobil: fotoğraf kartın tamamını kaplar */
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(to right, #11172b 0%, rgba(17, 23, 43, 0.6) 20%, transparent 60%);
          z-index: 1;

          @media (max-width: 575px) {
            background: none;
          }
        }

        .banner-photo-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center 15%;
          -webkit-mask-image: linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 15%, black 45%);
          mask-image: linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 15%, black 45%);

          @media (max-width: 575px) {
            -webkit-mask-image: none;
            mask-image: none;
            object-position: center 20%;
          }
        }

        /* Top Right Vertical Dots Indicator */
        /* Sağ üstte öne çıkan kullanıcıların avatar şeridi */
        .banner-vertical-dots {
          position: absolute;
          top: 16px;
          right: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          z-index: 5;

          @media (max-width: 575px) {
            top: 10px;
            right: 10px;
            flex-direction: row;
            gap: 6px;
          }

          .vertical-dot {
            width: 32px;
            height: 32px;
            padding: 0;
            border-radius: 50%;
            border: 2px solid rgba(255, 255, 255, 0.35);
            background: linear-gradient(160deg, #3b1f6e 0%, #1e1442 100%);
            overflow: hidden;
            cursor: pointer;
            opacity: 0.6;
            transition: transform 0.2s ease, opacity 0.2s ease, border-color 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-size: 12px;
            font-weight: 700;

            @media (max-width: 575px) {
              width: 26px;
              height: 26px;
            }

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            &:hover {
              opacity: 1;
              transform: scale(1.06);
            }

            &.active {
              opacity: 1;
              border-color: #ea03b2;
              box-shadow: 0 0 0 2px rgba(234, 3, 178, 0.35), 0 0 12px rgba(234, 3, 178, 0.55);
              transform: scale(1.08);
            }
          }
        }

        /* Önceki / sonraki okları: fotoğrafın üzerinde, hover'da belirginleşir */
        .banner-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 6;
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 1px solid rgba(255, 255, 255, 0.25);
          background: rgba(14, 10, 40, 0.55);
          backdrop-filter: blur(8px);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0.75;
          transition: opacity 0.2s ease, background 0.2s ease;

          &:hover {
            opacity: 1;
            background: rgba(234, 3, 178, 0.75);
          }

          &.banner-nav-prev { left: 12px; }
          &.banner-nav-next { right: 60px; }

          @media (max-width: 575px) {
            width: 32px;
            height: 32px;
            top: 32%; /* bilgi alanının üstünde, fotoğraf üzerinde kalsın */

            &.banner-nav-next { right: 10px; }
          }
        }

        /* Bottom Right Handwritten Script Quote */
        .banner-script-quote {
          position: absolute;
          bottom: 18px;
          right: 22px;
          z-index: 5;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          font-family: 'Caveat', 'Dancing Script', cursive, sans-serif;
          color: rgba(255, 255, 255, 0.96);
          transform: rotate(-5deg);
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);

          @media (max-width: 767px) {
            bottom: 12px;
            right: 16px;
          }

          @media (max-width: 575px) {
            bottom: 10px;
            right: 14px;
          }

          .quote-line-1 {
            font-size: 20px;
            font-weight: 700;
            line-height: 1.1;

            @media (max-width: 767px) {
              font-size: 17px;
            }

            @media (max-width: 575px) {
              font-size: 16px;
            }
          }

          .quote-line-2 {
            font-size: 22px;
            font-weight: 700;
            line-height: 1.1;
            display: flex;
            align-items: center;
            gap: 4px;

            @media (max-width: 767px) {
              font-size: 18px;
            }

            @media (max-width: 575px) {
              font-size: 17px;
            }

            .heart-doodle {
              font-size: 20px;
              color: #f43f5e;

              @media (max-width: 767px) {
                font-size: 16px;
              }
            }
          }
        }
      }
    }
  }
}

/* Category Filter Pills */
.filters-section {
  margin-bottom: 24px;

  .filter-pills-row {
    display: flex;
    align-items: center;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 6px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .category-pill {
      border: 1px solid rgba(170, 140, 255, 0.3);
      background: rgba(32, 22, 72, 0.72);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      color: rgba(236, 232, 255, 0.85);
      padding: 8px 18px;
      border-radius: 9999px;
      font-size: 13px;
      font-weight: 400; // Not bold!
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.2s ease;
      box-shadow: 0 2px 8px rgba(17, 23, 43, 0.08);
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .cat-pill-icon {
        stroke: currentColor;
        flex-shrink: 0;
      }

      &:hover:not(.active) {
        background: rgba(139, 92, 246, 0.2);
        color: #ffffff;
        border-color: rgba(190, 160, 255, 0.5);
      }

      /* Seçili kategori: menübardaki seçili sekmenin gradyanı */
      &.active {
        color: #ffffff;
        font-weight: 700;
        background: linear-gradient(135deg, rgba(234, 3, 178, 0.55) 0%, rgba(168, 85, 247, 0.45) 50%, rgba(99, 102, 241, 0.3) 100%);
        border-color: rgba(234, 3, 178, 0.6);
        box-shadow: 0 2px 14px rgba(234, 3, 178, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.25);

        .cat-pill-icon {
          stroke: #ffffff;
          filter: drop-shadow(0 0 4px rgba(234, 3, 178, 0.7));
        }
      }
    }
  }
}

/* Profiles Grid */
.grid-section {
  .profiles-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 18px;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 14px;
    }

    @media (max-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }

    @media (max-width: 400px) {
      gap: 8px;
    }
  }
}

/* Rush Card Component */
.rush-card {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: #1e1b4b;
  aspect-ratio: 0.7;
  cursor: pointer;
  transition: box-shadow 0.25s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  /* Kendi katmanında çizilsin: hover'da gradyan/yazı titremesin */
  transform: translateZ(0);
  backface-visibility: hidden;
  isolation: isolate;

  /* Kartı yukarı kaldırmak alttaki gradyanda kısa bir zıplamaya yol açıyordu;
     artık sadece gölge ve parıltı değişiyor */
  &:hover {
    box-shadow: 0 14px 28px rgba(0, 0, 0, 0.3), 0 0 0 1.5px rgba(234, 3, 178, 0.55), 0 0 22px rgba(234, 3, 178, 0.25);
  }

  .card-photo-box {
    position: absolute;
    inset: 0;

    .photo-img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s ease;
      will-change: transform;
      backface-visibility: hidden;
    }

    .photo-fallback {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 64px;
      font-weight: 700;
      color: rgba(255, 255, 255, 0.55);
      background: linear-gradient(160deg, #3b1f6e 0%, #1e1442 100%);
    }
  }

  &:hover .photo-img {
    transform: scale(1.03) translateZ(0);
  }

  /* Top Badges */
  .card-badges-top {
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: 2;

    .status-pill {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 8px;
      border-radius: 9999px;
      background: rgba(15, 23, 42, 0.65);
      backdrop-filter: blur(8px);
      color: #ffffff;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.3px;

      &.busy .status-dot {
        background: #f59e0b;
        box-shadow: 0 0 6px #f59e0b;
      }

      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 9999px;
        background: #22c55e;
        box-shadow: 0 0 6px #22c55e;

        &.recently {
          background: #f59e0b;
          box-shadow: 0 0 6px #f59e0b;
        }

        &.offline {
          background: #94a3b8;
          box-shadow: none;
        }
      }
    }

    .type-pill {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 3px 8px;
      border-radius: 8px;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #ffffff;

      &.is-ai {
        background: linear-gradient(135deg, #8b5cf6, #d946ef);
        box-shadow: 0 2px 8px rgba(217, 70, 239, 0.3);
      }

      &.is-real {
        background: linear-gradient(135deg, #10b981, #059669);
        box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
      }
    }
  }

  /* Bottom Content & Action Buttons */
  .card-info-bottom {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 14px 12px 12px;
    z-index: 2;
    background: linear-gradient(to top, rgba(15, 10, 25, 0.96) 0%, rgba(15, 10, 25, 0.72) 65%, transparent 100%);
    color: #ffffff;

    @media (max-width: 576px) {
      padding: 10px 8px 8px;
    }

    .user-primary-row {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 2px;

      .country-badge {
        font-size: 10px;
        font-weight: 800;
        color: rgba(255, 255, 255, 0.85);
        background: rgba(255, 255, 255, 0.15);
        padding: 1px 4px;
        border-radius: 4px;
      }

      .user-display-name {
        font-size: 14px;
        font-weight: 700;
        color: #ffffff;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        @media (max-width: 576px) {
          font-size: 13px;
        }
      }
    }

    .user-secondary-row {
      margin-bottom: 10px;

      @media (max-width: 576px) {
        margin-bottom: 6px;
      }

      .sub-info {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.65);

        @media (max-width: 576px) {
          font-size: 10px;
        }
      }
    }

    /* 4-Action Buttons Row */
    .card-actions-bar {
      display: flex;
      align-items: center;
      gap: 6px;

      @media (max-width: 576px) {
        gap: 4px;
      }

      .action-pill-btn {
        flex: 1;
        min-width: 0;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        border: none;
        border-radius: 9999px;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px);
        color: #ffffff;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
        padding: 0 8px;
        white-space: nowrap;

        @media (max-width: 576px) {
          height: 28px;
          padding: 0 4px;
          font-size: 10px;
          gap: 3px;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        &:active {
          transform: scale(0.96);
        }
      }

      .action-circle-btn {
        width: 32px;
        height: 32px;
        flex-shrink: 0;
        border-radius: 9999px;
        border: none;
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px);
        color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.15s ease;

        @media (max-width: 576px) {
          width: 28px;
          height: 28px;
        }

        .plus-icon {
          font-size: 16px;
          line-height: 1;
          font-weight: 700;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        &:active {
          transform: scale(0.92);
        }

        &.is-active {
          background: #7c3aed;
          color: #ffffff;
        }

        &.is-liked {
          background: rgba(239, 68, 68, 0.2);
        }
      }
    }
  }
}

/* Empty State & Spinners */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;

  .empty-icon-box {
    font-size: 40px;
    margin-bottom: 12px;
  }

  h3 {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 6px;
  }

  p {
    font-size: 14px;
    color: #94a3b8;
  }
}

.load-more-spinner {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}
</style>
